// src/components/BlogSection.jsx
import React, { useState, useEffect } from "react";
import BlogApi from "../../api/BlogApi";
import { API_URL } from "../../config/app-config";

const TAG_COLORS = {
  Growth:  { color: "#15803d", bg: "#f0fdf4" },
  Tips:    { color: "#a00030", bg: "#ffeef3" },
  Feature: { color: "#7c3aed", bg: "#f5f3ff" },
  Story:   { color: "#854f0b", bg: "#fffbeb" },
  Guide:   { color: "#0369a1", bg: "#eff6ff" },
  News:    { color: "#0f766e", bg: "#f0fdfa" },
};

// ── Image base URL — matches GET /auth/image/{uuid} ───────────────────────
const IMAGE_BASE_URL =  API_URL+"auth/image/";

function resolveThumbUrl(thumbnailUrl) {
  if (!thumbnailUrl || !thumbnailUrl.trim()) return null;
  if (
    thumbnailUrl.startsWith("http://") ||
    thumbnailUrl.startsWith("https://") ||
    thumbnailUrl.startsWith("blob:")
  ) {
    return thumbnailUrl;
  }
  return IMAGE_BASE_URL + thumbnailUrl.trim();
}

// ── Featured blogs hook ───────────────────────────────────────────────────
function useFeaturedBlogs() {
  const [blogs,   setBlogs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    const CACHE_KEY = "yk_featured_blogs";
    const CACHE_TTL = 5 * 60 * 1000;
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) { setBlogs(data); setLoading(false); return; }
      }
    } catch {}

    BlogApi.getFeaturedBlogs()
      .then((response) => {
        const raw = response?.data;
        let list = [];
        if (Array.isArray(raw?.data))            list = raw.data;
        else if (Array.isArray(raw))             list = raw;
        else if (Array.isArray(raw?.data?.data)) list = raw.data.data;
        setBlogs(list);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: list, ts: Date.now() })); } catch {}
      })
      .catch(() => setError("Failed to load blogs"))
      .finally(() => setLoading(false));
  }, []);

  return { blogs, loading, error };
}

// ── Thumbnail renderer ────────────────────────────────────────────────────
function BlogThumb({ blog, fontSize = "3.5rem" }) {
  const [err, setErr] = useState(false);
  const url = resolveThumbUrl(blog.thumbnailUrl);

  if (url && !err) {
    return (
      <img
        src={url}
        alt={blog.title}
        onError={() => setErr(true)}
        style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
      />
    );
  }
  return <span style={{ fontSize }}>{blog.emoji || "📝"}</span>;
}

// ── Skeleton ──────────────────────────────────────────────────────────────
function BlogSkeleton() {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))", gap:"1.5rem", marginTop:"2rem" }}>
      {[1,2,3].map(i => (
        <div key={i} style={{ background:"#fff", borderRadius:16, border:"1px solid #f0dde3", overflow:"hidden", opacity:.5 }}>
          <div style={{ height:160, background:"#f0dde3" }} />
          <div style={{ padding:"1.25rem" }}>
            <div style={{ height:12, background:"#f0dde3", borderRadius:6, marginBottom:10, width:"40%" }} />
            <div style={{ height:16, background:"#f0dde3", borderRadius:6, marginBottom:8 }} />
            <div style={{ height:16, background:"#f0dde3", borderRadius:6, marginBottom:8, width:"85%" }} />
            <div style={{ height:12, background:"#f0dde3", borderRadius:6, width:"60%" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Blog Card ─────────────────────────────────────────────────────────────
function BlogCard({ blog, onClick }) {
  const [hovered, setHovered] = useState(false);
  const tagStyle = TAG_COLORS[blog.tag] || { color:"#6b4550", bg:"#f0dde3" };

  return (
    <div onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background:"#fff", borderRadius:16, border:"1px solid #f0dde3",
        overflow:"hidden", cursor:"pointer", display:"flex", flexDirection:"column",
        boxShadow: hovered ? "0 8px 24px rgba(160,0,48,.12)" : "0 2px 8px rgba(0,0,0,.06)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        transition:"all .2s ease"
      }}>

      <div style={{
        height:160, background:blog.color||"#ffeef3",
        borderBottom:`1px solid ${blog.borderColor||"#f0dde3"}`,
        display:"flex", alignItems:"center", justifyContent:"center",
        overflow:"hidden", flexShrink:0
      }}>
        <BlogThumb blog={blog} fontSize="3.5rem" />
      </div>

      <div style={{ padding:"1.25rem", flex:1, display:"flex", flexDirection:"column" }}>
        <span style={{
          display:"inline-block", fontSize:".7rem", fontWeight:700,
          letterSpacing:".08em", textTransform:"uppercase",
          color:tagStyle.color, background:tagStyle.bg,
          padding:".2rem .7rem", borderRadius:20,
          marginBottom:".75rem", alignSelf:"flex-start"
        }}>
          {blog.tag}
        </span>
        <div style={{
          fontWeight:700, fontSize:"1rem", lineHeight:1.4, marginBottom:".6rem",
          color: hovered ? "#a00030" : "#1a0a0f", transition:"color .2s"
        }}>
          {blog.title}
        </div>
        <div style={{
          fontSize:".85rem", color:"#6b4550", lineHeight:1.6,
          marginBottom:".75rem", flex:1,
          display:"-webkit-box", WebkitLineClamp:3,
          WebkitBoxOrient:"vertical", overflow:"hidden"
        }}>
          {blog.excerpt}
        </div>
        <div style={{
          display:"flex", justifyContent:"space-between",
          fontSize:".75rem", color:"#b8929e", marginTop:"auto"
        }}>
          <span>{blog.author?.trim() ? blog.author : "Yuukke Team"}</span>
          <span>{blog.readTime}</span>
        </div>
        <div style={{
          marginTop:".65rem", fontSize:".8rem", fontWeight:700, color:"#a00030",
          opacity: hovered ? 1 : 0, transition:"opacity .2s"
        }}>
          Read article →
        </div>
      </div>
    </div>
  );
}

// ── Blog Modal ────────────────────────────────────────────────────────────
function BlogModal({ blog, onClose }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgErr,  setImgErr]  = useState(false);
  const tagStyle = TAG_COLORS[blog?.tag] || { color:"#6b4550", bg:"#f0dde3" };

  useEffect(() => {
    if (!blog?.id) return;
    setLoading(true);
    setImgErr(false);
    BlogApi.getBlogById(blog.id)
      .then(({ data }) => {
        const b = data?.data && typeof data.data === "object" && !Array.isArray(data.data)
          ? data.data : data;
        setDetail(b);
      })
      .catch(() => setDetail(blog))
      .finally(() => setLoading(false));
  }, [blog?.id]);

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const b        = detail || blog;
  const thumbUrl = resolveThumbUrl(b.thumbnailUrl);

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position:"fixed", inset:0, background:"rgba(26,10,15,.55)",
        zIndex:9999, display:"flex", alignItems:"flex-start",
        justifyContent:"center", overflowY:"auto", padding:"2rem 1rem"
      }}>
      <div style={{
        background:"#fff", borderRadius:20, width:"100%", maxWidth:740,
        boxShadow:"0 24px 64px rgba(160,0,48,.15)",
        marginBottom:"2rem", border:"1px solid #f0dde3"
      }}>

        {/* ── Modal header ── */}
        <div style={{
          background:b.color||"#ffeef3", borderRadius:"20px 20px 0 0",
          borderBottom:`1px solid ${b.borderColor||"#f0dde3"}`,
          position:"relative", overflow:"hidden"
        }}>
          {thumbUrl && !imgErr ? (
            /* ── Full-width thumbnail banner ── */
            <div style={{ width:"100%", height:220, position:"relative" }}>
              <img
                src={thumbUrl}
                alt={b.title}
                onError={() => setImgErr(true)}
                style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
              />
              <div style={{
                position:"absolute", inset:0,
                background:"linear-gradient(to bottom, transparent 40%, rgba(26,10,15,.6))"
              }} />
              <div style={{ position:"absolute", bottom:0, left:0, padding:"1.5rem 2rem" }}>
                <div style={{ fontSize:"2rem", marginBottom:".4rem" }}>{b.emoji}</div>
                <span style={{
                  display:"inline-block", fontSize:".72rem", fontWeight:700,
                  color:tagStyle.color, background:tagStyle.bg,
                  padding:".25rem .75rem", borderRadius:20, marginBottom:".5rem"
                }}>{b.tag}</span>
              </div>
              <button onClick={onClose} style={{
                position:"absolute", top:"1rem", right:"1rem",
                background:"rgba(255,255,255,.85)", border:"none", borderRadius:8,
                padding:".3rem .75rem", cursor:"pointer", fontSize:".85rem",
                fontWeight:600, color:"#6b4550"
              }}>✕ Close</button>
            </div>
          ) : (
            /* ── No thumbnail — colored header ── */
            <div style={{ padding:"2rem 2rem 1.5rem" }}>
              <button onClick={onClose} style={{
                position:"absolute", top:"1rem", right:"1rem",
                background:"rgba(255,255,255,.7)", border:"none", borderRadius:8,
                padding:".3rem .75rem", cursor:"pointer", fontSize:".85rem",
                fontWeight:600, color:"#6b4550"
              }}>✕ Close</button>
              <div style={{ fontSize:"2.5rem", marginBottom:".75rem" }}>{b.emoji}</div>
              <span style={{
                display:"inline-block", fontSize:".72rem", fontWeight:700,
                color:tagStyle.color, background:tagStyle.bg,
                padding:".25rem .75rem", borderRadius:20, marginBottom:".75rem"
              }}>{b.tag}</span>
            </div>
          )}

          <div style={{ padding:"1.25rem 2rem 1.5rem" }}>
            <h2 style={{
              fontFamily:"'Playfair Display',Georgia,serif",
              fontSize:"clamp(1.4rem,3vw,1.9rem)", fontWeight:800,
              color:"#1a0a0f", lineHeight:1.3, margin:"0 0 .75rem"
            }}>{b.title}</h2>
            <div style={{ display:"flex", gap:"1rem", fontSize:".8rem", color:"#6b4550", flexWrap:"wrap" }}>
              <span>✍️ {b.author?.trim() ? b.author : "Yuukke Team"}</span>
              <span>⏱ {b.readTime}</span>
              <span>📅 {b.createdAt}</span>
            </div>
          </div>
        </div>

        {/* ── Modal body ── */}
        <div style={{ padding:"2rem" }}>
          {loading ? (
            <div style={{ textAlign:"center", padding:"2rem", color:"#6b4550" }}>
              Loading article...
            </div>
          ) : (
            <>
              <p style={{
                fontSize:"1.1rem", color:"#6b4550", lineHeight:1.8,
                borderLeft:"3px solid #a00030", paddingLeft:"1rem",
                marginBottom:"1.75rem", fontStyle:"italic"
              }}>{b.excerpt}</p>
              <div style={{ fontSize:".95rem", lineHeight:1.8, color:"#1a0a0f" }}
                dangerouslySetInnerHTML={{ __html: b.content }} />
            </>
          )}

          <div style={{
            marginTop:"2rem", background:"#fff9f7",
            border:"1px solid #f0dde3", borderRadius:14,
            padding:"1.25rem 1.5rem", textAlign:"center"
          }}>
            <div style={{ fontWeight:700, fontSize:"1rem", marginBottom:".4rem", color:"#1a0a0f" }}>
              Ready to grow your service business?
            </div>
            <div style={{ fontSize:".85rem", color:"#6b4550", marginBottom:"1rem" }}>
              Join 500+ women providers on Yuukke — ₹4,999 lifetime deal
            </div>
            <button
              onClick={() => { onClose(); setTimeout(() => document.getElementById("yk2-getstarted")?.scrollIntoView({ behavior:"smooth" }), 300); }}
              style={{
                background:"#a00030", color:"#fff", border:"none",
                borderRadius:10, padding:".75rem 2rem", fontWeight:700,
                fontSize:".9rem", cursor:"pointer"
              }}>
              Start for free →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────
export default function BlogSection({ t }) {
  const { blogs, loading, error } = useFeaturedBlogs();
  const [selectedBlog, setSelectedBlog] = useState(null);

  return (
    <>
      <div id="yk2-blog" style={{ background:"#fff9f7", padding:"5rem 2rem", maxWidth:"100%" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ fontSize:".8rem", fontWeight:700, letterSpacing:".12em",
            textTransform:"uppercase", color:"#a00030", marginBottom:"1rem" }}>
            {t("Blog & Stories")}
          </div>
          <h2 style={{
            fontFamily:"'Playfair Display',Georgia,serif",
            fontSize:"clamp(1.8rem,4vw,2.5rem)", fontWeight:800,
            color:"#1a0a0f", marginBottom:"1rem", lineHeight:1.2
          }}>
            {t("Learn from women who've been there.")}
          </h2>
          <p style={{ fontSize:"1rem", color:"#6b4550", marginBottom:"2.5rem", maxWidth:560 }}>
            {t("Real stories, practical tips, and guides to help you grow your service business.")}
          </p>

          {loading && <BlogSkeleton />}

          {error && !loading && (
            <div style={{ textAlign:"center", padding:"2rem", color:"#6b4550", fontSize:".9rem" }}>
              ⚠️ Could not load blogs right now. Check back soon.
            </div>
          )}

          {!loading && !error && blogs.length === 0 && (
            <div style={{ textAlign:"center", padding:"2rem", color:"#6b4550" }}>
              <div style={{ fontSize:"2rem", marginBottom:".5rem" }}>📝</div>
              <p>Blog coming soon — check back later!</p>
            </div>
          )}

          {!loading && blogs.length > 0 && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:"1.5rem", marginBottom:"2.5rem" }}>
              {blogs.map(blog => (
                <BlogCard key={blog.id} blog={blog} onClick={() => setSelectedBlog(blog)} />
              ))}
            </div>
          )}

          <div style={{ textAlign:"center" }}>
            <a href="/yuukke-service-blogs/blogs"
              style={{
                display:"inline-flex", alignItems:"center", gap:".5rem",
                color:"#a00030", textDecoration:"none", borderRadius:10,
                padding:".75rem 2rem", fontWeight:700, fontSize:".95rem",
                transition:"opacity .2s",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity=".85"}
              onMouseLeave={e => e.currentTarget.style.opacity="1"}>
              {t("Read all articles →")}
            </a>
          </div>
        </div>
      </div>

      {selectedBlog && <BlogModal blog={selectedBlog} onClose={() => setSelectedBlog(null)} />}
    </>
  );
}